<template>
  <div class="dashboard-shell" :class="{ 'dashboard-shell--navigation-open': drawerState && isOverlayNavigation }" :style="dashboardShellStyle">
    <main class="dashboard-shell__main">
      <header v-if="hasDashboardChrome" class="dashboard-toolbar">
        <div class="dashboard-toolbar__primary">
          <v-btn
            v-if="isOverlayNavigation"
            class="dashboard-toolbar__button"
            color="white"
            icon
            large
            aria-label="Navigation öffnen"
            title="Navigation öffnen"
            @click="toggleNavbar">
            <v-icon>mdi-menu</v-icon>
          </v-btn>

          <v-card-title v-if="routeMeta.displayedTitle" class="dashboard-toolbar__title white--text headline">
            {{ routeMeta.displayedTitle }}
          </v-card-title>

          <v-spacer></v-spacer>

          <v-btn
            v-if="hasStudioPicker || hasDatePicker"
            class="dashboard-toolbar__panel-button primary"
            icon
            large
            aria-label="Panel öffnen"
            title="Panel öffnen"
            @click="$router.push('/panel')">
            <v-icon class="white--text">mdi-parking</v-icon>
          </v-btn>
        </div>

        <div v-if="hasStudioPicker || hasDatePicker" class="dashboard-toolbar__controls">
          <div v-if="hasDatePicker" class="dashboard-toolbar__control dashboard-toolbar__control--date">
            <v-menu ref="menu" v-model="datePickerMenu" dark :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  dark
                  filled
                  hide-details
                  :value="formattedDatePickerRange"
                  label="Zeitraum"
                  readonly
                  v-bind="attrs"
                  v-on="on"></v-text-field>
              </template>
              <v-date-picker
                ref="datePicker"
                v-model="datePickerRange"
                locale="de-CH"
                range
                :type="routeMeta.monthsOnly ? 'month' : 'date'"
                @update:active-picker="activePickerChanged"
                @change="$refs.menu.save(datePickerRange)"
                no-title
                scrollable></v-date-picker>
            </v-menu>
          </div>

          <div
            v-if="(studios.length > 1 || (teams.length && routeMeta.includeTeams)) && hasStudioPicker"
            class="dashboard-toolbar__control dashboard-toolbar__control--studio">
            <v-select
              v-if="routeMeta.multiStudio"
              v-model="selectedStudios"
              :items="selectStudioItems"
              small-chips
              dark
              filled
              hide-details
              chips
              label="Studios"
              multiple
              item-text="name"
              item-value="_id">
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index === 0">
                  <span>{{ item.name }}</span>
                </v-chip>
                <span v-if="index === 1" class="grey--text text-caption">(+{{ selectedStudios.length - 1 }} weitere...)</span>
              </template>
            </v-select>

            <v-select
              v-else
              v-model="selectedStudioSingle"
              :items="selectStudioItems"
              small-chips
              dark
              filled
              hide-details
              chips
              label="Studio"
              item-text="name"
              item-value="_id">
              <template v-slot:item="{ item }">
                <span>
                  {{ item.name }}
                  <span v-if="item.studio" class="orange--text">(Center {{ getStudioName(item.studio) }})</span>
                </span>
              </template>
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index === 0">
                  <span>
                    {{ item.name }}
                    <span v-if="item.studio" class="orange--text">(Center {{ getStudioName(item.studio) }})</span>
                  </span>
                </v-chip>
              </template>
            </v-select>

            <v-btn
              v-if="routeMeta.multiStudio && studios.length > 1"
              class="dashboard-toolbar__select-all white--text"
              text
              small
              @click="toggleStudioSelection">
              {{ selectedStudios.length === studios.length ? "alle Studios abwählen" : "alle Studios anwählen" }}
            </v-btn>
          </div>
        </div>
      </header>

      <div v-if="!hasDashboardChrome && isOverlayNavigation" class="dashboard-floating-actions" role="toolbar" aria-label="Dashboard-Aktionen">
        <v-btn
          class="dashboard-floating-actions__button dashboard-floating-actions__button--menu"
          color="white"
          icon
          aria-label="Navigation öffnen"
          title="Navigation öffnen"
          @click="toggleNavbar">
          <v-icon>mdi-menu</v-icon>
        </v-btn>
      </div>

      <section class="dashboard-content" :class="isFullscreenView ? 'dashboard-content--fullscreen' : 'dashboard-content--framed'">
        <div v-if="loading" class="dashboard-loader" role="status" aria-label="Dashboard wird geladen">
          <button
            class="dashboard-loader__home"
            type="button"
            aria-label="Zur Dashboard-Startseite"
            title="Zur Dashboard-Startseite"
            @click="$router.push('/dashboard')">
            <lottie-player
              class="dashboard-loader__animation"
              mode="normal"
              name="roody_icon"
              autoplay
              loop
              src="/img/lottiefiles/roody_animated.json"></lottie-player>
          </button>
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-if="isDashboardHome" v-show="!loading" class="dashboard-home white--text">
          <dashboard-home
            :quick-link-options="dashboardQuickLinkOptions"
            :quick-link-options-ready="dashboardQuickLinkOptionsReady"
            @open-quick-link="openDashboardQuickLink"></dashboard-home>
        </div>

        <div v-else v-show="!loading" class="dashboard-route" :class="isFullscreenView ? 'dashboard-route--fullscreen' : 'dashboard-route--framed'">
          <router-view v-bind="routerViewProps" @finishedloading="finishedLoading"></router-view>
        </div>
      </section>
    </main>

    <left-sidebar
      ref="leftSidebar"
      :drawerstate.sync="drawerState"
      @dashboard-quick-link-options="setDashboardQuickLinkOptions"
      @layout-width="handleSidebarLayoutWidth"></left-sidebar>
  </div>
</template>

<script>
import api from "../../api"
import leftSidebar from "./sidebar/leftSidebar.vue"
import dashboardHome from "./home/DashboardHome.vue"
import { mapState, mapActions } from "vuex"
import userService from "../../services/userService"
export default {
  name: "dashboard",
  components: { leftSidebar, dashboardHome },
  data() {
    return {
      drawerState: false,
      sidebarSpace: 0,
      dashboardQuickLinkOptions: [],
      dashboardQuickLinkOptionsReady: false,
      selectedStudios: [],
      userService,
      selectedStudioSingle: "",
      studios: [],
      allStudios: [], // NEU: Backup aller geladenen Studios
      teams: [],
      loading: false,
      datePickerRange: [this.$moment().startOf("month").format("YYYY-MM-DD"), this.$moment().endOf("month").format("YYYY-MM-DD")],
      datePickerMenu: false,
      loadingTimer: null,
    }
  },
  async created() {
    let sidebarPinned = true
    try {
      const storedPinnedPreference = localStorage.getItem("roody-sidebar-pinned")
      sidebarPinned = storedPinnedPreference === null ? true : storedPinnedPreference === "true"
    } catch (error) {
      sidebarPinned = true
    }

    this.sidebarSpace = this.$vuetify.breakpoint.smAndDown ? 0 : sidebarPinned ? 296 : 76
    if (this.routeMeta.needsLoader) this.loading = true
    this.fetchStudios()
    this.fetchTeams()

    this.setNavigationbarVisibility(false)
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    routeMeta() {
      return this.$route.meta || {}
    },
    hasStudioPicker() {
      if (this.routeMeta.studioPicker === false) return false
      return Boolean(this.routeMeta.studioPicker || this.routeMeta.statistics || this.routeMeta.studioOnly)
    },
    hasDatePicker() {
      if (this.routeMeta.datePicker === false) return false
      return Boolean(this.routeMeta.datePicker || this.routeMeta.datePickerOnly || (this.routeMeta.statistics && !this.routeMeta.hideRange))
    },
    isDashboardHome() {
      return this.$route.name === "dashboardHome"
    },
    hasDashboardChrome() {
      return this.isDashboardHome || this.hasStudioPicker || this.hasDatePicker
    },
    isFullscreenView() {
      return !this.isDashboardHome && !this.hasStudioPicker && !this.hasDatePicker
    },
    isOverlayNavigation() {
      return this.$vuetify.breakpoint.smAndDown
    },
    dashboardShellStyle() {
      return { "--sidebar-space": `${this.sidebarSpace}px` }
    },
    routerViewProps() {
      if (!this.hasDashboardChrome) return { studios: this.studios }

      return {
        datePickerRange: this.datePickerRange,
        studios: this.filteredStudios,
        selectedStudioSingle: this.selectedStudioSingle,
      }
    },
    formattedDatePickerRange() {
      if (!this.datePickerRange.length) return "Ganzer Zeitraum"
      if (this.datePickerRange[0] && !this.datePickerRange[1] && this.$route.meta.monthsOnly) {
        let pickedMonth = this.$moment(this.datePickerRange[0], "YYYY-MM")
        this.$set(this.datePickerRange, 0, pickedMonth.startOf("month").format("YYYY-MM-DD"))
        this.$set(this.datePickerRange, 1, pickedMonth.endOf("month").format("YYYY-MM-DD"))
      }
      if (!this.datePickerRange[1]) return this.$moment(this.datePickerRange[0], "YYYY-MM-DD").format("DD.MM.YYYY") + " - Datum auswählen"
      if (this.$moment(this.datePickerRange[0], "YYYY-MM-DD").isAfter(this.$moment(this.datePickerRange[1]))) {
        let olddatePickerRange0 = this.datePickerRange[0]
        this.datePickerRange[0] = this.datePickerRange[1]
        this.datePickerRange[1] = olddatePickerRange0
      }
      if (
        this.$moment(this.datePickerRange[0], "YYYY-MM-DD").startOf("month").format("YYYY-MM-DD") === this.datePickerRange[0] &&
        this.$moment(this.datePickerRange[1], "YYYY-MM-DD").endOf("month").format("YYYY-MM-DD") === this.datePickerRange[1] &&
        this.$moment(this.datePickerRange[0], "YYYY-MM-DD").format("YYYY-MM") ===
          this.$moment(this.datePickerRange[1], "YYYY-MM-DD").format("YYYY-MM")
      ) {
        return this.$moment(this.datePickerRange[0], "YYYY-MM-DD").format("MMMM YYYY")
      } else if (
        this.$moment().isSame(this.$moment(this.datePickerRange[0], "YYYY-MM-DD"), "day") &&
        this.$moment().isSame(this.$moment(this.datePickerRange[1], "YYYY-MM-DD"), "day")
      ) {
        return "Heute"
      } else if (
        this.$moment().subtract(1, "days").isSame(this.$moment(this.datePickerRange[0], "YYYY-MM-DD"), "day") &&
        this.$moment().subtract(1, "days").isSame(this.$moment(this.datePickerRange[1], "YYYY-MM-DD"), "day")
      ) {
        return "Gestern"
      } else if (this.datePickerRange[0] === this.datePickerRange[1]) {
        return this.$moment(this.datePickerRange[0], "YYYY-MM-DD").format("DD.MM.YYYY")
      } else {
        let enddatePickerRange = this.$moment().isSame(this.$moment(this.datePickerRange[1], "YYYY-MM-DD"), "day")
          ? "Heute"
          : this.$moment(this.datePickerRange[1], "YYYY-MM-DD").format("DD.MM.YYYY")
        return this.$moment(this.datePickerRange[0], "YYYY-MM-DD").format("DD.MM.YYYY") + " - " + enddatePickerRange
      }
      return (
        this.$moment(this.datePickerRange[0], "YYYY-MM-DD").format("DD.MM.YYYY") +
        " - " +
        this.$moment(this.datePickerRange[1], "YYYY-MM-DD").format("DD.MM.YYYY")
      )
    },
    selectStudioItems() {
      let studs
      if (this.routeMeta.includeTeams) {
        let result = []

        // Loop through each studio
        this.studios.forEach((studio) => {
          // Add the current studio to the result
          result.push(studio)

          // Find its teams and add them to the result
          this.teams.forEach((team) => {
            if (team.studio === studio._id) {
              result.push(team)
            }
          })
        })

        // Add remaining teams which don't have a studio property to the result
        this.teams.forEach((team) => {
          if (!team.studio) {
            result.push(team)
          }
        })

        studs = result
      } else {
        studs = this.studios
      }
      return studs
    },
    filteredStudios() {
      let newStudioArray = this.routeMeta.includeTeams ? this.studios.concat(this.teams) : this.studios

      return newStudioArray.filter((studio) => this.selectedStudios.includes(studio._id))
    },
  },
  mounted() {
    if (this.loading) this.scheduleLoadingFallback()
  },
  watch: {
    $route(to, from) {
      this.clearLoadingTimer()
      this.loading = Boolean(to.meta.needsLoader)
      if (this.loading) this.scheduleLoadingFallback()

      // NEU: Filter bei Routenwechsel rein/raus aus gf-stats neu anwenden
      const wasGfStats = from.path.includes("gf-stats")
      const isGfStats = to.path.includes("gf-stats")
      if (wasGfStats !== isGfStats) {
        this.applyGfStatsFilter()
      }
    },
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    setDashboardQuickLinkOptions(options) {
      this.dashboardQuickLinkOptions = Array.isArray(options) ? options : []
      this.dashboardQuickLinkOptionsReady = true
    },
    openDashboardQuickLink(option) {
      const sidebar = this.$refs.leftSidebar
      if (sidebar && typeof sidebar.openDashboardQuickLink === "function") {
        sidebar.openDashboardQuickLink(option)
        return
      }

      if (option && option.location) this.$router.push(option.location)
    },
    handleSidebarLayoutWidth(width) {
      const nextWidth = Number(width)
      if (!Number.isFinite(nextWidth)) return
      this.sidebarSpace = Math.max(0, nextWidth)
    },
    getStudioName(studioId) {
      const studio = this.studios.find((item) => item._id === studioId)
      return studio ? studio.name : "Unbekannt"
    },
    clearLoadingTimer() {
      if (!this.loadingTimer) return
      window.clearTimeout(this.loadingTimer)
      this.loadingTimer = null
    },
    scheduleLoadingFallback() {
      this.clearLoadingTimer()
      this.loadingTimer = window.setTimeout(() => {
        this.loading = false
        this.loadingTimer = null
      }, 5000)
    },
    toggleStudioSelection() {
      if (this.selectedStudios.length === this.studios.length) {
        this.selectedStudios = []
      } else {
        this.selectedStudios = this.studios.map((studio) => studio._id)
      }
    },
    activePickerChanged(value) {
      if (value === "YEAR" && this.$refs.datePicker && this.$refs.datePicker.tableDate) {
        let pickedYear = this.$moment(this.$refs.datePicker.tableDate, "YYYY")
        this.$set(this.datePickerRange, 0, pickedYear.startOf("year").format("YYYY-MM-DD"))
        this.$set(this.datePickerRange, 1, pickedYear.endOf("year").format("YYYY-MM-DD"))
      }
      if (value === "DATE" && this.$refs.datePicker && this.$refs.datePicker.tableDate) {
        if (this.routeMeta.monthsOnly) return
        let pickedMonth = this.$moment(this.$refs.datePicker.tableDate, "YYYY-MM")
        this.$set(this.datePickerRange, 0, pickedMonth.startOf("month").format("YYYY-MM-DD"))
        this.$set(this.datePickerRange, 1, pickedMonth.endOf("month").format("YYYY-MM-DD"))
      }
    },
    toggleNavbar() {
      this.drawerState = !this.drawerState
    },
    finishedLoading() {
      this.clearLoadingTimer()
      this.loading = false
    },
    async fetchStudios() {
      let studios = await api.fetchAllowedAdminStudios()

      studios.forEach((studio) => {
        if (!studio.location) studio.location = { lat: 0, long: 0 }
        if (!studio.vacationDays) studio.vacationDays = []
      })

      this.allStudios = studios
      this.applyGfStatsFilter()
    },
    applyGfStatsFilter() {
      const userEmail = this.userService.getEmail()
      let filteredStudios = this.allStudios

      if (this.$route.path.includes("gf-stats")) {
        const gfStatsAccess = {
          "b.piraino@hotmail.com": ["arbon"],
          "ruben.ten.cate@wellcomefit.ch": ["basel"],
          "mel.thiene@gmail.com": ["buelach"],
          "munalinanaji@yahoo.com": ["dietlikon"],
          "cornelia.beck-hasler@bluewin.ch": ["frauefeldwest"],
          "sandra.braendle@wellcomefit.ch": ["herisau"],
          "cstonewoman@hispeed.ch": ["kreuzlingen"],
          "nicoleburger39@gmail.com": ["muri"],
          "tivo88@hotmail.com": ["netstal"],
          "mel.hinnen@gmail.com": ["oerlikon", "wallisellen", "wetzikonnord", "wetzikon"],
          "m.niggli2424@gmail.com": ["obfelden"],
          "rw@wintifit.ch": ["pfungen"],
          "jb@wellcomefit.ch": ["rikon", "winterthur"],
          "ao@wellcomefit.ch": ["sirnach"],
          "fg@wellcomefit.ch": ["stgalleneinstein", "stgallenost"],
          "elena.petrushanko@wellcomefit.ch": ["steckborn"],
          "chantalfink@sunrise.ch": ["wil"],
        }

        const allowedCodes = gfStatsAccess[userEmail]
        if (allowedCodes) {
          // Filter aus storeStudios anwenden (wie bisher)
          filteredStudios = this.storeStudios.filter((studio) => allowedCodes.includes(studio.code))
        }
      }

      this.studios = filteredStudios
      this.selectedStudios = filteredStudios.map((studio) => studio._id)
      this.selectedStudioSingle = filteredStudios[0]?._id
    },
    async fetchStudiosOld() {
      let studios = await api.fetchAllowedAdminStudios()

      const userEmail = this.userService.getEmail()
      console.log(userEmail)

      this.selectedStudioSingle = studios[0]._id
      studios.forEach((studio) => {
        if (!studio.location) studio.location = { lat: 0, long: 0 }
        if (!studio.vacationDays) studio.vacationDays = []
        this.selectedStudios.push(studio._id)

        this.studios.push(studio)
      })
    },
    async fetchTeams() {
      if (this.userService.hasRight("tt_teams")) this.teams = await api.fetchAllowedTeams()
      else this.teams = await api.fetchAllowedTeamStudios()

      if (this.routeMeta.includeTeams) {
        this.teams.forEach((team) => {
          if (!team.vacationDays) team.vacationDays = []

          this.selectedStudios.push(team._id)
        })
      }
    },
  },
  beforeDestroy() {
    this.clearLoadingTimer()
  },
}
</script>
<style scoped>
.dashboard-shell {
  --dashboard-bg: #181a1e;
  --dashboard-surface: rgba(34, 37, 43, 0.82);
  --dashboard-border: rgba(255, 255, 255, 0.09);
  --dashboard-text: #f5f7fa;
  --dashboard-muted: rgba(245, 247, 250, 0.62);
  --dashboard-ease: cubic-bezier(0.22, 1, 0.36, 1);
  --dashboard-content-padding: clamp(6px, 0.65vw, 10px);

  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 !important;
  padding: 0 !important;
  overflow-x: hidden;
  overflow-x: clip;
  color: var(--dashboard-text);
  background: #1e1e1e;
}

.dashboard-shell__main {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 100vh;
  min-height: 100dvh;
  padding-inline-start: var(--sidebar-space, 76px);
  background: transparent;
  transition: padding-inline-start 260ms var(--dashboard-ease);
}

.dashboard-toolbar {
  position: relative;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 14px 20px;
  padding: clamp(16px, 2vw, 28px) clamp(16px, 2.5vw, 36px) 16px;
}

.dashboard-toolbar__primary {
  display: flex;
  flex: 1 1 420px;
  align-items: center;
  min-width: 0;
  min-height: 52px;
}

.dashboard-toolbar__title {
  min-width: 0;
  padding: 0 12px !important;
  font-size: clamp(1.65rem, 3vw, 2.2rem) !important;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.035em;
  overflow-wrap: anywhere;
}

.dashboard-toolbar__button,
.dashboard-toolbar__panel-button,
.dashboard-floating-actions__button {
  flex: 0 0 auto;
  border: 1px solid var(--dashboard-border);
  border-radius: 15px !important;
  background: rgba(31, 34, 39, 0.78) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  transition: transform 180ms var(--dashboard-ease), border-color 180ms ease, background-color 180ms ease;
}

.dashboard-toolbar__panel-button {
  background: var(--v-primary-base) !important;
  border-color: rgba(255, 255, 255, 0.14);
}

.dashboard-toolbar__controls {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 12px;
  min-width: 0;
}

.dashboard-toolbar__control {
  min-width: 0;
}

.dashboard-toolbar__control--date {
  width: min(250px, 100%);
}

.dashboard-toolbar__control--studio {
  width: min(360px, 100%);
}

.dashboard-toolbar__select-all {
  min-height: 34px;
  margin-top: 6px;
  border-radius: 11px;
  letter-spacing: 0.01em;
  text-transform: none;
}

.dashboard-toolbar__control ::v-deep .v-input__slot {
  min-height: 52px;
  border: 1px solid var(--dashboard-border);
  border-radius: 15px 15px 12px 12px !important;
  background: var(--dashboard-surface) !important;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.16);
  backdrop-filter: blur(18px) saturate(135%);
  -webkit-backdrop-filter: blur(18px) saturate(135%);
}

.dashboard-toolbar__control ::v-deep .v-chip {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.09);
}

.dashboard-content {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  color: var(--dashboard-text);
  background: transparent;
}

.dashboard-content--framed {
  padding: 0 clamp(16px, 2.5vw, 36px) clamp(24px, 4vw, 52px);
}

.dashboard-content--fullscreen {
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0;
  padding: var(--dashboard-content-padding);
  border: 0;
}

.dashboard-route {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.dashboard-route--framed {
  min-height: calc(100vh - 120px);
  min-height: calc(100dvh - 120px);
}

.dashboard-route--fullscreen {
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0;
  padding: 0;
  border-radius: 0;
}

.dashboard-floating-actions {
  position: fixed;
  z-index: 15;
  top: max(14px, calc(env(safe-area-inset-top, 0px) + 8px));
  right: max(16px, env(safe-area-inset-right, 0px));
  left: calc(var(--sidebar-space, 76px) + 16px + env(safe-area-inset-left, 0px));
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
  transition: left 260ms var(--dashboard-ease), opacity 160ms ease;
}

.dashboard-shell--navigation-open .dashboard-floating-actions {
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
}

.dashboard-floating-actions__button {
  width: 44px !important;
  height: 44px !important;
  pointer-events: auto;
}

.dashboard-loader {
  position: absolute;
  z-index: 8;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  min-height: 70vh;
  color: var(--dashboard-muted);
  background: rgba(24, 26, 30, 0.72);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.dashboard-loader__home {
  display: block;
  padding: 0;
  border: 0;
  border-radius: 22px;
  color: inherit;
  background: transparent;
  cursor: pointer;
  transition: transform 180ms var(--dashboard-ease);
}

.dashboard-loader__animation {
  display: block;
  width: 130px;
  height: 150px;
}

.dashboard-toolbar__button:focus-visible,
.dashboard-toolbar__panel-button:focus-visible,
.dashboard-floating-actions__button:focus-visible,
.dashboard-loader__home:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.88);
  outline-offset: 3px;
}

@media (hover: hover) and (pointer: fine) {
  .dashboard-toolbar__button:hover,
  .dashboard-toolbar__panel-button:hover,
  .dashboard-floating-actions__button:hover,
  .dashboard-loader__home:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.18);
  }
}

.dashboard-home {
  width: 100%;
  background: transparent !important;
}

@media (max-width: 959px) {
  .dashboard-shell__main {
    padding-inline-start: 0;
  }

  .dashboard-toolbar {
    padding: 14px 14px 18px;
  }

  .dashboard-toolbar__primary,
  .dashboard-toolbar__controls {
    flex-basis: 100%;
  }

  .dashboard-toolbar__controls {
    justify-content: flex-start;
  }

  .dashboard-content--framed {
    padding: 0 max(14px, env(safe-area-inset-right, 0px)) max(24px, env(safe-area-inset-bottom, 0px)) max(14px, env(safe-area-inset-left, 0px));
  }

  .dashboard-floating-actions {
    right: max(14px, env(safe-area-inset-right, 0px));
    left: max(14px, env(safe-area-inset-left, 0px));
  }
}

@media (max-width: 599px) {
  .dashboard-toolbar__title {
    font-size: clamp(1.4rem, 7vw, 1.8rem) !important;
  }

  .dashboard-toolbar__control--date,
  .dashboard-toolbar__control--studio {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-shell__main,
  .dashboard-floating-actions,
  .dashboard-toolbar__button,
  .dashboard-toolbar__panel-button,
  .dashboard-floating-actions__button {
    transition-duration: 0.01ms !important;
  }
}
</style>
